import { Label } from '@cian/ui-kit';
import cn from 'classnames';
import * as React from 'react';

import { ILabelTooltip, LabelTooltip } from './LabelTooltip';
import * as styles from './Labels.css';
import { getBackgroundColor, getTextColor } from './utils';
import { useDragScroll } from '../../hooks/useDragScroll';
import { useScrollEdges } from '../../hooks/useScrollEdges';
import { EColor, IPhotoLabel } from '../../repositories/dailyrent/entities/photo_label/PhotoLabel';

interface IProps {
  labels: IPhotoLabel[];
  className?: string;
  isScrollEnabled?: boolean;
}

const TOOLTIP_MAP: Partial<Record<EColor, ILabelTooltip>> = {
  blueVerifiedByCian: {
    description:
      'Убедились, что информация в объявлении верна — оценили видео, фото объекта или дополнительно проанализировали объявление',
    link: 'https://support.cian.ru/ru/knowledge_base/article/7516/category/463',
    linkText: 'Подробнее',
  },
  blueInstantBooking: {
    description: 'Это жильё можно забронировать без переговоров с владельцем и долгого ожидания подтверждения брони',
  },
};

export const Labels = ({ labels, className, isScrollEnabled = false }: IProps) => {
  const key = React.useId();
  // Временно скрываем лейбл "только на циан" на фронте пока не сделана задача CD-179745
  const filteredLabels = labels.filter(({ color }) => color !== EColor.BlueOnlyCian);

  const handleClick = React.useCallback((e: React.MouseEvent) => {
    if (e.stopPropagation) {
      e.stopPropagation();
    }
  }, []);

  // Скролл лейблов мышью (drag + колесо); вызываем до раннего return ниже — Rules of Hooks.
  const containerRef = React.useRef<HTMLDivElement>(null);
  const leftEdgeRef = React.useRef<HTMLDivElement>(null);
  const rightEdgeRef = React.useRef<HTMLDivElement>(null);

  useDragScroll(containerRef, isScrollEnabled);
  useScrollEdges({ containerRef, leftEdgeRef, rightEdgeRef, isEnabled: filteredLabels.length > 0 && isScrollEnabled });

  if (!filteredLabels.length) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={cn(styles['container'], !isScrollEnabled && styles['scroll-disabled'], className)}
    >
      {/* Сентинелы — мишени IntersectionObserver (useScrollEdges), живут весь lifecycle контейнера */}
      <div ref={leftEdgeRef} className={styles['edge-sentinel']} aria-hidden="true" />
      {filteredLabels.map(({ name, color }) => {
        return (
          <LabelTooltip key={`label_${key}_${color}`} tooltip={TOOLTIP_MAP[color]}>
            <Label onClick={handleClick} background={getBackgroundColor(color)} color={getTextColor(color)}>
              {name}
            </Label>
          </LabelTooltip>
        );
      })}
      <div ref={rightEdgeRef} className={styles['edge-sentinel']} aria-hidden="true" />
    </div>
  );
};
